import paper from "paper";
import levelData from "../../data/levelData.json";
import { runWavemakerSimulation } from "./wavemakerSimulation.js";
import { loadWavemakerFonts } from "./fonts.js";
import { createTranslate, setDirectionRoot, setLocale, setTranslate } from "../i18n";

const LOCALE_UPDATE_MESSAGE = "wavemaker/locale-update";
const PROGRESS_REPORT_OPEN_MESSAGE = "wavemaker/progress-report-open";

export function createWavemakerGame(options = {}) {
  if (typeof document === "undefined") {
    throw new Error("[Wavemaker] Document is not available in this environment.");
  }

  const canvas = resolveCanvas(options.canvas ?? options.canvasSelector);
  void loadWavemakerFonts();
  setDirectionRoot(canvas.closest(".wavemaker-root") ?? canvas.parentElement ?? canvas);
  setTranslate(options.translate ?? createTranslate(options.locale));

  const scope = new paper.PaperScope();
  scope.setup(canvas);
  applyMeasuredViewSize(scope, canvas);

  const simulation = runWavemakerSimulation(scope, levelData, {
    onLevelLabelChange: options.onLevelLabelChange,
    onTimeSpeedLabelChange: options.onTimeSpeedLabelChange,
  });
  const destroyResponsiveView = createResponsiveViewController(scope, canvas);

  const handleLocaleMessage = (event) => {
    if (event.origin !== window.location.origin) {
      return;
    }
    if (!event.data || typeof event.data !== "object") {
      return;
    }
    const payload = event.data;
    if (payload.type === LOCALE_UPDATE_MESSAGE) {
      if (typeof payload.locale !== "string") {
        return;
      }
      setLocale(payload.locale);
      return;
    }
    if (payload.type === PROGRESS_REPORT_OPEN_MESSAGE) {
      simulation?.openProgressReport?.();
    }
  };

  window.addEventListener("message", handleLocaleMessage);

  return {
    pause() {
      simulation?.setPaused?.(true);
    },
    resume() {
      simulation?.setPaused?.(false);
    },
    setShowReferenceLines(enabled) {
      simulation?.setShowReferenceLines?.(enabled);
    },
    setReferenceLineSpacingRatio(spacingRatio) {
      simulation?.setReferenceLineSpacingRatio?.(spacingRatio);
    },
    destroy() {
      window.removeEventListener("message", handleLocaleMessage);
      simulation?.destroy?.();
      destroyResponsiveView?.();
      try {
        scope.project?.clear?.();
        scope.remove?.();
      } catch {
        // ignore cleanup errors
      }
      const context = canvas.getContext("2d");
      context?.clearRect(0, 0, canvas.width, canvas.height);
    },
    scope,
  };
}

function resolveCanvas(target) {
  if (target && typeof target === "object" && "nodeType" in target) {
    if (target.nodeName?.toLowerCase() === "canvas") {
      return target;
    }
    throw new Error("[Wavemaker] The provided element is not a canvas.");
  }

  if (typeof target === "string") {
    const node = document.querySelector(target);
    if (node instanceof HTMLCanvasElement) {
      return node;
    }
    throw new Error(`[Wavemaker] Could not find canvas with selector "${target}".`);
  }

  const fallback = document.getElementById("myCanvas");
  if (fallback instanceof HTMLCanvasElement) {
    return fallback;
  }

  throw new Error("[Wavemaker] canvas element is required to create the game.");
}

function measureViewport(canvas) {
  const viewportElement = canvas.parentElement;
  const bounds = viewportElement?.getBoundingClientRect();
  const widthFromBounds = bounds ? Math.round(bounds.width) : 0;
  const heightFromBounds = bounds ? Math.round(bounds.height) : 0;

  const width = widthFromBounds > 0 ? widthFromBounds : Math.round(window.innerWidth);
  const height = heightFromBounds > 0 ? heightFromBounds : Math.round(window.innerHeight);

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  return { width, height };
}

function applyMeasuredViewSize(scope, canvas) {
  const measured = measureViewport(canvas);
  if (!measured) {
    return;
  }

  canvas.style.width = `${measured.width}px`;
  canvas.style.height = `${measured.height}px`;
  scope.view.viewSize = new scope.Size(measured.width, measured.height);
}

function createResponsiveViewController(scope, canvas) {
  const view = scope.view;
  const worldWidth = view.viewSize.width;
  const worldHeight = view.viewSize.height;
  const worldCenter = new scope.Point(worldWidth * 0.5, worldHeight * 0.5);

  let rafId = 0;
  let isDisposed = false;

  function fitViewToViewport() {
    rafId = 0;
    if (isDisposed) {
      return;
    }

    const measured = measureViewport(canvas);
    if (!measured) {
      return;
    }

    canvas.style.width = `${measured.width}px`;
    canvas.style.height = `${measured.height}px`;

    if (
      measured.width !== Math.round(view.viewSize.width) ||
      measured.height !== Math.round(view.viewSize.height)
    ) {
      view.viewSize = new scope.Size(measured.width, measured.height);
    }

    const zoom = Math.min(measured.width / worldWidth, measured.height / worldHeight);
    view.zoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
    view.center = worldCenter;
    if (typeof view.update === "function") {
      view.update();
    }
  }

  function scheduleFit() {
    if (isDisposed || rafId !== 0) {
      return;
    }

    rafId = window.requestAnimationFrame(fitViewToViewport);
  }

  const resizeObserver =
    typeof ResizeObserver === "function" ? new ResizeObserver(() => scheduleFit()) : null;

  if (resizeObserver) {
    resizeObserver.observe(canvas.parentElement ?? canvas);
    resizeObserver.observe(document.documentElement);
  }
  window.addEventListener("resize", scheduleFit);
  window.addEventListener("orientationchange", scheduleFit);
  document.addEventListener("fullscreenchange", scheduleFit);
  scheduleFit();

  return () => {
    isDisposed = true;
    if (rafId !== 0) {
      window.cancelAnimationFrame(rafId);
    }
    resizeObserver?.disconnect();
    window.removeEventListener("resize", scheduleFit);
    window.removeEventListener("orientationchange", scheduleFit);
    document.removeEventListener("fullscreenchange", scheduleFit);
  };
}
